import React, { FC, useEffect, useState } from 'react';
import styled from 'astroturf/react';
import ModalBase from 'js/components/modals/Modal';
import { Button } from 'js/design_system/Button';
import { TextButton } from 'js/design_system/TextButton';
import { Heading as HeadingBase } from 'js/design_system/Heading';
import { Colors } from 'js/design_system/Color';
import CloseIcon from 'js/design_system/icons/close.svg';
import { createUserBulkFollowing } from 'js/social/api/user_bulk_following_api';
import { Suggestion } from 'js/social/shared/types';
import { Spinner } from 'js/social/shared/CreateSocialItem/Spinner';
import BulkFollowSuggestion, { FollowSuggestion } from './BulkFollowSuggestion';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import {
  CardType,
  getListModalHeader,
  getListModalImpressionSource,
  getFollowingSource,
} from './bulk_follow_modal_helper';

const SpinnerContainer = styled.div`
  text-align: center;
`;

const Modal = styled(ModalBase)`
  width: 100%;
  max-width: 90%;
  padding: 24px;
  color: $ds-black;

  @include for-tablet-up {
    max-width: 633px;
  }
`;

const CloseButton = styled(TextButton)`
  height: 24px;
  width: 24px;
  position: absolute;
  right: 12px;
  top: 12px;
  padding: 0;
`;

const Heading = styled(HeadingBase)`
  font-size: 20px;
  line-height: 28px;
  text-align: center;
  max-width: 409px;
  margin: 0 auto 24px;
  padding: 16px 0 4px;
`;

const SuggestionList = styled.div`
  height: 400px;
  overflow-y: scroll;
  margin-bottom: 20px;
  padding: 0 16px;
`;

const UpdateButtonContainer = styled.div``;

const Cta = styled(Button)`
  display: flex;
  max-width: 427px;
  width: 100%;
  margin: 0 auto;
  font-size: 14px;
`;

type Props = {
  open: boolean;
  asyncFollowSuggSource?: () => Promise<{ suggestions: Suggestion[] }>;
  initialFollowSuggestions: FollowSuggestion[];
  cardType: CardType;
  onClose: (followingsAdded: boolean) => void;
  topic?: {
    name: string;
    score?: number;
  };
};

const BulkFollowListModal: FC<Props> = ({
  open,
  asyncFollowSuggSource,
  initialFollowSuggestions,
  cardType,
  onClose,
  topic,
}) => {
  const [followSuggestions, setFollowSuggestions] = useState<
    FollowSuggestion[]
  >(initialFollowSuggestions);
  const [loading, setLoading] = useState(false);
  const [followingsAdded, setFollowingsAdded] = useState(false);

  useEffect(() => {
    if (asyncFollowSuggSource) {
      const fetchData = async () => {
        setLoading(true);
        const response = await asyncFollowSuggSource();
        const respFollowSuggestions = response.suggestions.map(
          (sugg: Suggestion) => {
            const suggReason =
              sugg.sources && sugg.metadata
                ? {
                    sources: sugg.sources,
                    metadata: sugg.metadata,
                  }
                : undefined;
            return {
              id: sugg.user.id,
              displayName: sugg.user.display_name,
              photoUrl: sugg.user.photo,
              universityName: sugg.user.department?.university?.name,
              departmentName: sugg.user.department?.name,
              suggestionReason: suggReason,
              following: sugg.user.followed,
            };
          },
        );
        setFollowSuggestions(respFollowSuggestions);
        setLoading(false);
      };
      fetchData();
    }
  }, [asyncFollowSuggSource]);

  const followAll = async () => {
    const suggestionDetailMap: {
      [key: number]: { [key: string]: string | number };
    } = {};

    followSuggestions.forEach((suggestion) => {
      suggestionDetailMap[suggestion.id] =
        suggestion.suggestionReason !== undefined
          ? { suggestion_reason: suggestion.suggestionReason.sources[0] }
          : {
              topic: topic?.name ?? '',
              topic_score: topic?.score ?? 0,
              individual_follow: 'false',
            };
    });

    return await createUserBulkFollowing(
      followSuggestions.map((followSugg) => followSugg.id),
      getFollowingSource(cardType),
      false,
      suggestionDetailMap,
    );
  };

  return (
    <>
      {open && (
        <Modal onClose={() => onClose(followingsAdded)}>
          <Heading size="sm">
            {getListModalHeader(cardType, topic?.name)}
          </Heading>

          <CloseButton
            aria-label="Close"
            onClick={() => onClose(followingsAdded)}
          >
            <CloseIcon color={Colors.GrayDark} />
          </CloseButton>
          {loading ? (
            <SpinnerContainer>
              <Spinner />
            </SpinnerContainer>
          ) : (
            <>
              <SuggestionList>
                {followSuggestions.map((followSugg, index) => (
                  <BulkFollowSuggestion
                    key={`modal-follow-suggestion-${followSugg.id}`}
                    index={index}
                    onFollow={() => setFollowingsAdded(true)}
                    followableUser={followSugg}
                    following={followSugg.following}
                    followingSource={getFollowingSource(cardType)}
                    impressionSource={getListModalImpressionSource(
                      cardType,
                      'follow',
                    )}
                    topic={topic}
                  />
                ))}
              </SuggestionList>

              <UpdateButtonContainer>
                <ImpressionWatcher
                  source={getListModalImpressionSource(cardType, 'follow_A')}
                >
                  <Cta
                    onClick={async () => {
                      await followAll();
                      setFollowingsAdded(true);
                      onClose(true);
                    }}
                  >
                    Follow all
                  </Cta>
                </ImpressionWatcher>
              </UpdateButtonContainer>
            </>
          )}
        </Modal>
      )}
    </>
  );
};

export default BulkFollowListModal;
